import { defineStore } from "pinia";
import { computed, ComputedRef, ref } from "vue";
import { ContactGroup } from "@/models/contact-group";
import { ContactFilter } from "@/models/contact-filter";
import { contactGroupService } from "@/services/contact-group.service";
import router from "@/router";
import { ROUTE_NAME } from "@/router/routes.constants";
import { findIndex } from "underscore";

export interface ContactGroupModuleState {
  contactGroups: {
    content: ContactGroup[];
    empty?: boolean;
    first?: boolean;
    last?: boolean;
    number?: number;
    numberOfElements?: number;
    pageable?: {
      pageNumber: number;
      pageSize: number;
      sort: [];
      offset: number;
      paged: boolean;
      unpaged: boolean;
    };
    size?: number;
    sort?: [];
    totalElements?: number;
    totalPages?: number;
  };
  isContactGroupsLoading: boolean;
  contactGroupsHasError: boolean;
  filterQuery: any;
  contactTabCount: {
    contacts: number;
    groups: number;
    filters: number;
    roles: number;
  };
  contactFilter: {
    content: ContactFilter[];
    empty?: boolean;
    first?: boolean;
    last?: boolean;
    number?: number;
    numberOfElements?: number;
    pageable?: {
      pageNumber: number;
      pageSize: number;
      sort: [];
      offset: number;
      paged: boolean;
      unpaged: boolean;
    };
    size?: number;
    sort?: [];
    totalElements?: number;
    totalPages?: number;
  };
  contactFilterForm: any;
}
export const useContactGroupStore = defineStore("contactGroup", () => {
  // STATES
  const contactGroups = ref<ContactGroupModuleState["contactGroups"]>({
    content: [],
  });
  const isContactGroupsLoading = ref<boolean>(false);
  const contactGroupsHasError = ref<boolean>(false);
  const filterQuery = ref<ContactGroupModuleState["filterQuery"]>([]);
  const contactTabCount = ref<ContactGroupModuleState["contactTabCount"]>({
    contacts: 0,
    groups: 0,
    filters: 0,
    roles: 0,
  });
  const contactFilter = ref<ContactGroupModuleState["contactFilter"]>({
    content: [],
  });
  const contactFilterForm =
    ref<ContactGroupModuleState["contactFilterForm"]>(null);

  const setContactGroups = async (payload: any) => {
    isContactGroupsLoading.value = true;
    await contactGroupService
      .getContactGroups(
        payload.contactFolderId,
        payload.pageIndex,
        payload.pageSize,
        payload.search,
        payload.sortOrder,
        payload.sortField,
      )
      .then(({ data }) => {
        contactGroups.value = data;
        contactTabCount.value.groups = data.totalElements || 0;
      })
      .catch(() => {
        contactGroupsHasError.value = true;
        return [];
      })
      .finally(() => {
        isContactGroupsLoading.value = false;
      });
  };
  const updateContactGroup = async (payload: {
    group: ContactGroup;
    contactFolderId: string;
  }): Promise<void> => {
    isContactGroupsLoading.value = true;
    return new Promise((resolve, reject) => {
      contactGroupService
        .updateContactGroup(payload.group, payload.contactFolderId)
        .then(({ data }) => {
          updateGroup(data);
          resolve();
        })
        .catch(reject)
        .finally(() => (isContactGroupsLoading.value = false));
    });
  };
  const updateGroup = (group: ContactGroup) => {
    const index = findIndex(
      contactGroups.value.content,
      (contactGroup: ContactGroup) =>
        contactGroup.contactGroupId === group.contactGroupId,
    );
    index != -1 && (contactGroups.value.content[index] = { ...group });
  };
  const createContactGroup = async (payload: {
    group: ContactGroup;
    contactFolderId: string;
    addMembersAfterGroupCreation: boolean;
  }): Promise<void> => {
    setContactGroupLoading(true);
    return new Promise((resolve, reject) => {
      contactGroupService
        .createContactGroup(payload.group, payload.contactFolderId)
        .then(async ({ data }) => {
          saveContactGroup(data);
          resolve();
          payload.addMembersAfterGroupCreation &&
            (await router.push({
              name: ROUTE_NAME.ADD_CONTACT_GROUP_MEMBERS,
              params: { contactGroupId: data.contactGroupId },
            }));
        })
        .catch(reject)
        .finally(() => setContactGroupLoading(false));
    });
  };

  const saveContactGroup = (group: any) => {
    contactGroups.value.content.push({ ...group });
  };

  const setContactGroupLoading = (value: boolean) => {
    isContactGroupsLoading.value = value;
  };
  const setContactGroupHasError = (value: boolean) => {
    contactGroupsHasError.value = value;
  };

  const deleteContactGroup = async (group: ContactGroup): Promise<void> => {
    isContactGroupsLoading.value = true;
    return new Promise((resolve, reject) => {
      contactGroupService
        .deleteContactGroup(group)
        .then(() => {
          deleteGroup(group.contactGroupId);
          resolve();
        })
        .catch((error) => {
          reject(new Error(error));
        })
        .finally(() => (isContactGroupsLoading.value = false));
    });
  };

  const deleteGroup = (contactGroupId: string) => {
    const index = findIndex(
      contactGroups.value?.content,
      (contactGroup: ContactGroup) =>
        contactGroup.contactGroupId === contactGroupId,
    );
    index != -1 && contactGroups.value?.content.splice(index, 1);
  };

  const updateSetQuery = (group: any) => {
    filterQuery.value = group;
  };

  const setContactFilterForm = (group: any) => {
    contactFilterForm.value = group;
  };

  const setUpdateTabCount = (
    countData: ContactGroupModuleState["contactTabCount"],
  ) => {
    contactTabCount.value = countData;
  };

  const setContactFilters = (filters: any) => {
    contactFilter.value = filters;
  };

  const fetchContactFilter = async (payload: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      contactGroupService
        .getContactFilters(
          payload.page || 0,
          payload.size || 100,
          payload.search,
          payload.folderId,
          payload.sortField,
          payload.sortOrder,
        )
        .then(({ data }) => {
          contactFilter.value = data;
          contactTabCount.value.filters = data.content.length;
          resolve();
        })
        .catch((error) => {
          reject(new Error(error));
        });
    });
  };

  // GETTERS
  const getTabCount: ComputedRef<ContactGroupModuleState["contactTabCount"]> =
    computed(() => contactTabCount.value);

  const getContactFilter: ComputedRef<
    ContactGroupModuleState["contactFilter"]
  > = computed(() => contactFilter.value);

  const getContactFilterForm: ComputedRef<
    ContactGroupModuleState["contactFilterForm"]
  > = computed(() => contactFilterForm.value);

  const getContactGroupData: ComputedRef<
    ContactGroupModuleState["contactGroups"]
  > = computed(() => contactGroups.value);

  const getContactGroupLoading: ComputedRef<boolean> = computed(
    () => isContactGroupsLoading.value,
  );

  const getContactGroupHasError: ComputedRef<boolean> = computed(
    () => contactGroupsHasError.value,
  );

  const getFilterQuery: ComputedRef<any> = computed(() => filterQuery.value);

  return {
    setContactGroups,
    createContactGroup,
    deleteContactGroup,
    updateContactGroup,
    updateSetQuery,
    setContactFilterForm,
    setUpdateTabCount,
    setContactFilters,
    fetchContactFilter,
    setContactGroupHasError,
    getTabCount,
    getContactFilter,
    getContactFilterForm,
    getContactGroupData,
    getContactGroupLoading,
    getContactGroupHasError,
    getFilterQuery,
  };
});
